import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppWindow,
  Building2,
  ChevronsDownUp,
  ChevronsUpDown,
  Layers,
  Network,
  RefreshCw,
  Server,
  UserRound,
} from 'lucide-react';

import forgeService from '../../../services/forgeService';
import adminUsersService from '../../../services/adminUsersService';
import { Button } from '../../Common/button';
import {
  Forge,
  ForgeApplication,
  ForgeClient,
  OrganizationWithUsers,
} from '../../../types';

import { buildDiagramGraph, nodeId } from './layout';
import { ForgeDiagram } from './ForgeDiagram';
import { toneClasses } from './status';

export const DiagramTab: React.FC = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [clients, setClients] = useState<ForgeClient[]>([]);
  const [forges, setForges] = useState<Forge[]>([]);
  const [apps, setApps] = useState<ForgeApplication[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationWithUsers[]>([]);

  // Collapsed node ids — default empty (everything expanded).
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, f, a, o] = await Promise.all([
        forgeService.listClients(),
        forgeService.listForges(),
        forgeService.listApplications(),
        adminUsersService.getOrganizationsWithMembers(),
      ]);
      setClients(c);
      setForges(f);
      setApps(a);
      setOrganizations(o);
    } catch (err) {
      console.error('Failed to load diagram data', err);
      setError(t('forgeDashboard.diagram.loadError', 'Failed to load diagram data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggleCollapse = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsed(new Set()), []);

  const collapseAll = useCallback(() => {
    const all = new Set<string>();
    clients.forEach((c) => all.add(nodeId('client', c.client_id)));
    forges.forEach((f) => all.add(nodeId('forge', f.forge_id)));
    setCollapsed(all);
  }, [clients, forges]);

  const graph = useMemo(
    () =>
      buildDiagramGraph({
        clients,
        forges,
        apps,
        organizations,
        collapsed,
        onToggleCollapse,
      }),
    [clients, forges, apps, organizations, collapsed, onToggleCollapse]
  );

  const linkedUsersCount = useMemo(() => {
    let n = 0;
    organizations.forEach((o) => {
      if (o.forge_client_id) n += (o.users || []).length;
    });
    return n;
  }, [organizations]);

  const linkedOrgsCount = useMemo(
    () => organizations.filter((o) => !!o.forge_client_id).length,
    [organizations]
  );

  const successTone = toneClasses('success');
  const infoTone = toneClasses('info');
  const warningTone = toneClasses('warning');
  const dangerTone = toneClasses('danger');
  const mutedTone = toneClasses('muted');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Network className="h-4 w-4" />
          <span>
            {t(
              'forgeDashboard.diagram.summary',
              '{{users}} users · {{orgs}} orgs · {{clients}} clients · {{forges}} forges · {{apps}} applications',
              {
                users: linkedUsersCount,
                orgs: linkedOrgsCount,
                clients: clients.length,
                forges: forges.length,
                apps: apps.length,
              }
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandAll} className="gap-2">
            <ChevronsUpDown className="h-4 w-4" />
            {t('forgeDashboard.diagram.expandAll', 'Expand all')}
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} className="gap-2">
            <ChevronsDownUp className="h-4 w-4" />
            {t('forgeDashboard.diagram.collapseAll', 'Collapse all')}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} className="gap-2">
            <RefreshCw className={['h-4 w-4', loading ? 'animate-spin' : ''].join(' ')} />
            {t('global.refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-destructive">
          {error}
        </div>
      )}

      {/* Legend */}
      <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5 font-medium text-foreground/80">
          {t('forgeDashboard.diagram.legend.title', 'Legend')}:
        </div>
        <div className="flex items-center gap-1.5">
          <UserRound className="h-3.5 w-3.5" />
          <span>{t('forgeDashboard.diagram.legend.user', 'Etabli user')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Building2 className="h-3.5 w-3.5" />
          <span>{t('forgeDashboard.diagram.legend.org', 'Organization')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Server className="h-3.5 w-3.5" />
          <span>{t('forgeDashboard.diagram.legend.client', 'Forge client')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" />
          <span>{t('forgeDashboard.diagram.legend.forge', 'Forge')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <AppWindow className="h-3.5 w-3.5" />
          <span>{t('forgeDashboard.diagram.legend.application', 'Application')}</span>
        </div>
        <div className="mx-2 h-4 w-px bg-border" />
        <div className="flex items-center gap-1.5">
          <span className={['inline-block h-2 w-2 rounded-full', successTone.dot].join(' ')} />
          <span>{t('forgeDashboard.diagram.status.healthy', 'Deployed / healthy')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={['inline-block h-2 w-2 rounded-full', infoTone.dot].join(' ')} />
          <span>{t('forgeDashboard.diagram.status.inProgress', 'In progress')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={['inline-block h-2 w-2 rounded-full', warningTone.dot].join(' ')} />
          <span>{t('forgeDashboard.diagram.status.stopped', 'Stopped')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={['inline-block h-2 w-2 rounded-full', dangerTone.dot].join(' ')} />
          <span>{t('forgeDashboard.diagram.status.error', 'Error')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={['inline-block h-2 w-2 rounded-full', mutedTone.dot].join(' ')} />
          <span>{t('forgeDashboard.diagram.status.unknown', 'Unknown')}</span>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="h-[70vh] w-full">
          {loading ? (
            <div className="h-full w-full flex items-center justify-center text-muted-foreground">
              <RefreshCw className="h-5 w-5 animate-spin mr-2" />
              {t('forgeDashboard.diagram.loading', 'Loading diagram…')}
            </div>
          ) : graph.nodes.length === 0 ? (
            <div className="h-full w-full flex items-center justify-center text-muted-foreground text-sm">
              {t(
                'forgeDashboard.diagram.empty',
                'No forge clients, forges or applications to display yet.'
              )}
            </div>
          ) : (
            <ForgeDiagram graph={graph} />
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {t(
          'forgeDashboard.diagram.hint',
          'Use the chevrons on forge clients and forges to collapse or expand their subtrees.'
        )}
      </p>
    </div>
  );
};

export default DiagramTab;
